import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Spinner } from 'reactstrap';
import Session from './auth/Session';
import AuthFunctions from './services/AuthService';
import { BsX, BsPlus, BsTrash } from 'react-icons/bs';
import { TimeEntryConfig } from './util/timeEntryConstants';

// -- Helpers
const todayLocalDate = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Builds the list of selectable dates: today, plus the previous
// (NUM_DAYS - 1) WORKABLE days (Sundays are skipped and don't count,
// so the window quietly extends one extra calendar day further back
// whenever it crosses a Sunday). Never includes a future date.
// Today itself is always shown as-is, even if today is a Sunday.
const buildAvailableDates = (numDays) => {
    const pad = (n) => String(n).padStart(2, '0');
    const toStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const cursor = new Date();
    const dates = [toStr(cursor)];

    while (dates.length < numDays) {
        cursor.setDate(cursor.getDate() - 1);
        if (cursor.getDay() === 0) continue; // Sunday (0) - skip, doesn't count
        dates.push(toStr(cursor));
    }

    return dates.reverse(); // oldest first, today last
};

const formatDayLabel = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
};

const formatPillLabel = (dateStr, isToday) => {
    if (isToday) return 'Today';
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
};

// Calculates total hours across all time entry rows.
const calcTotalHours = (crewSections) => {
    let total = 0;
    for (const section of crewSections) {
        for (const job of section.jobs) {
            for (const row of job.rows) {
                if (row.timeIn && row.timeOut) {
                    const [inH, inM] = row.timeIn.split(':').map(Number);
                    const [outH, outM] = row.timeOut.split(':').map(Number);
                    const diff = (outH * 60 + outM) - (inH * 60 + inM);
                    if (diff > 0) total += diff / 60;
                }
            }
        }
    }
    return Math.round(total * 100) / 100;
};

const isLight = (hex) => {
    if (!hex) return false;
    const h = hex.replace('#', '');
    if (h.length !== 6) return false;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55;
};

const toInputTime = (val) => {
    if (!val) return '';
    return val.length >= 5 ? val.substring(0, 5) : val;
};

// -- Builds the initial crewSections state from the API response.
// Each row now carries its db id (null for brand-new rows) and approved flag.
const buildCrewSections = (crewsWithEvents, savedEntries = []) =>
    crewsWithEvents.map((crew) => ({
        crewId: crew.id,
        crewName: crew.name,
        crewColor: crew.colorHex,
        jobs: [
            ...crew.todaysEvents.map((evt) => {
                const matches = savedEntries.filter(
                    (e) => e.crewId === crew.id && e.scheduledEventId === evt.id
                );
                return {
                    scheduledEventId: evt.id,
                    label: `${evt.customerName ? evt.customerName + ' - ' : ''}${evt.jobName || 'Untitled Job'}`,
                    rows: matches.length > 0
                        ? matches.map((e) => ({
                            id: e.id ?? null,
                            timeIn: toInputTime(e.timeIn),
                            timeOut: toInputTime(e.timeOut),
                            nurseryTaskId: e.nurseryTaskId ?? null,
                            approved: !!e.approved,
                        }))
                        : [{ id: null, timeIn: '', timeOut: '', nurseryTaskId: null, approved: false }],
                };
            }),
            {
                scheduledEventId: null,
                label: 'Nursery Time',
                rows: (() => {
                    const matches = savedEntries.filter(
                        (e) => e.crewId === crew.id && e.scheduledEventId === null
                    );
                    return matches.length > 0
                        ? matches.map((e) => ({
                            id: e.id ?? null,
                            timeIn: toInputTime(e.timeIn),
                            timeOut: toInputTime(e.timeOut),
                            nurseryTaskId: e.nurseryTaskId ?? null,
                            approved: !!e.approved,
                        }))
                        : [{ id: null, timeIn: '', timeOut: '', nurseryTaskId: null, approved: false }];
                })(),
            },
        ],
}));

// -- TimeEntry Page
const TimeEntry = () => {
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [crewSections, setCrewSections] = useState([]);
    const [availableDates] = useState(() => buildAvailableDates(TimeEntryConfig.NUM_DAYS));
    const [dateStr, setDateStr] = useState(todayLocalDate);
    const [nurseryTasks, setNurseryTasks] = useState([]);

    const todayStr = todayLocalDate();

    useEffect(() => {
        async function isAuth() {
            const isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) navigate('/login');
        }
        isAuth();
    }, [navigate]);

    const authHeaders = () => {
        const token = Session.getToken();
        return token
            ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
            : { 'Content-Type': 'application/json' };
    };

    const loadCrews = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [crewsRes, entriesRes, tasksRes] = await Promise.all([
                fetch(`api/Crew/GetForTimeEntry?date=${dateStr}`, { headers: authHeaders() }),
                fetch(`api/TimeEntry/GetForDate?date=${dateStr}`, { headers: authHeaders() }),
                fetch('api/TimeEntry/GetNurseryTasks', { headers: authHeaders() })
            ]);

            if (!crewsRes.ok) throw new Error('Failed to load your schedule.');
            if (!entriesRes.ok) throw new Error('Failed to load your saved time entries.');
            if (!tasksRes.ok) throw new Error('Failed to load nursery tasks.');


            const [crewsData, savedEntries, nurseryTasks] = await Promise.all([
                crewsRes.json(),
                entriesRes.json(),
                tasksRes.json()
            ]);

            setNurseryTasks(nurseryTasks);
            setCrewSections(buildCrewSections(crewsData, savedEntries));
        } catch (e) {
            setError(e.message || 'Failed to load your schedule.');
        } finally {
            setLoading(false);
        }
    }, [dateStr]);

    useEffect(() => {
        loadCrews();
    }, [loadCrews]);

    // -- Row mutations
    const updateRow = (crewIdx, jobIdx, rowIdx, field, value) => {
        setCrewSections((prev) => {
            const next = prev.map((c, ci) => ci !== crewIdx ? c : {
                ...c,
                jobs: c.jobs.map((j, ji) => ji !== jobIdx ? j : {
                    ...j,
                    rows: j.rows.map((r, ri) => ri !== rowIdx ? r : { ...r, [field]: value }),
                }),
            });
            return next;
        });
    };

    const addRow = (crewIdx, jobIdx) => {
        setCrewSections((prev) =>
            prev.map((c, ci) => ci !== crewIdx ? c : {
                ...c,
                jobs: c.jobs.map((j, ji) => ji !== jobIdx ? j : {
                    ...j,
                    rows: [...j.rows, { id: null, timeIn: '', timeOut: '', nurseryTaskId: null, approved: false }],
                }),
            })
        );
    };

    const removeRow = (crewIdx, jobIdx, rowIdx) => {
        setCrewSections((prev) =>
            prev.map((c, ci) => ci !== crewIdx ? c : {
                ...c,
                jobs: c.jobs.map((j, ji) => ji !== jobIdx ? j : {
                    ...j,
                    rows: j.rows.filter((_, ri) => ri !== rowIdx),
                }),
            })
        );
    };

    // -- Submit
    const handleSubmit = async () => {
        setError('');
        setSuccessMsg('');

        // Build flat list of entries.
        //  - Approved rows are ALWAYS included
        //  - New/unapproved rows are skipped only if completely blank.
        const entries = [];
        for (const section of crewSections) {
            for (const job of section.jobs) {
                for (const row of job.rows) {
                    const isBlank = !row.timeIn && !row.timeOut;
                    if (!row.approved && isBlank) continue;

                    entries.push({
                        id: row.id ?? null,
                        crewId: section.crewId,
                        scheduledEventId: job.scheduledEventId,
                        timeIn: row.timeIn || null,
                        timeOut: row.timeOut || null,
                        nurseryTaskId: row.nurseryTaskId || null,
                        approved: !!row.approved,
                    });
                }
            }
        }

        if (entries.length === 0) {
            setError('Please enter at least one time entry before submitting.');
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch('api/TimeEntry/SubmitDay', {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ date: dateStr, entries }),
            });
            if (!response.ok) throw new Error('Failed to submit time entries.');
            setSuccessMsg('Time entries submitted successfully!');

            // Reload so newly-inserted rows pick up their db ids (and any rows a
            // supervisor approved in the meantime lock correctly).
            await loadCrews();
        } catch (e) {
            setError(e.message || 'Failed to submit time entries.');
        } finally {
            setSubmitting(false);
        }
    };

    const totalHours = calcTotalHours(crewSections);

    return (
        <>
            {error && (
                <Row>
                    <Col>
                        <div className="alert alert-danger alert-dismissible mb-0" role="alert">
                            {error}
                            <button type="button" className="btn-close" onClick={() => setError('')} aria-label="Close" />
                        </div>
                    </Col>
                </Row>
            )}
            {successMsg && (
                <Row>
                    <Col>
                        <div className="alert alert-success alert-dismissible mb-0" role="alert">
                            {successMsg}
                            <button type="button" className="btn-close" onClick={() => setSuccessMsg('')} aria-label="Close" />
                        </div>
                    </Col>
                </Row>
            )}

            <Container className="container-main">
                {/* Header */}
                <div style={{ textAlign: 'center', margin: '1.5rem 0 1rem' }}>
                    <h3 style={{ margin: 0 }}>{formatDayLabel(dateStr)}</h3>
                </div>

                {/* Day picker: today + previous (NUM_DAYS - 1) days. Never shows a future date. */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    flexWrap: 'wrap',
                    marginBottom: '1.5rem',
                }}>
                    {availableDates.map((d) => (
                        <Button
                            key={d}
                            size="sm"
                            color={d === dateStr ? 'primary' : 'secondary'}
                            outline={d !== dateStr}
                            onClick={() => setDateStr(d)}
                            disabled={loading || submitting}
                        >
                            {formatPillLabel(d, d === todayStr)}
                        </Button>
                    ))}
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                        <Spinner />
                    </div>
                ) : (
                    <>
                        {crewSections.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '3rem 1rem' }}>
                                <p>You are not assigned to any active crews.</p>
                            </div>
                        ) : (
                            <>
                                {crewSections.map((section, crewIdx) => (
                                    <CrewSection
                                        key={section.crewId}
                                        section={section}
                                        crewIdx={crewIdx}
                                        onUpdateRow={updateRow}
                                        onAddRow={addRow}
                                        onRemoveRow={removeRow}
                                        nurseryTasks={nurseryTasks}
                                    />
                                ))}

                                {/* Total + Submit */}
                                <div style={{
                                    textAlign: 'center',
                                    marginTop: '1.5rem',
                                    marginBottom: '3rem',
                                }}>
                                    <p style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '1rem' }}>
                                        Total Hours: {totalHours}
                                    </p>
                                    <Button
                                        color="primary"
                                        size="md"
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                    >
                                        {submitting
                                            ? <Spinner size="sm" />
                                            : `Submit Time for ${dateStr === todayStr ? 'Today' : formatDayLabel(dateStr)}`}
                                    </Button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </Container>
        </>
    );
};

// -- CrewSection
function CrewSection({ section, crewIdx, onUpdateRow, onAddRow, onRemoveRow, nurseryTasks }) {
    const accentColor = section.crewColor || '#3788d8';
    const headerTextColor = isLight(accentColor) ? '#1a1a1a' : '#ffffff';

    return (
        <div style={{ marginBottom: '2rem' }}>
            {/* Crew header */}
            <div style={{
                backgroundColor: accentColor,
                borderRadius: '8px 8px 0 0',
                padding: '0.6rem 1rem',
            }}>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: headerTextColor }}>
                    {section.crewName}
                </span>
            </div>

            {/* Job rows */}
            <div style={{
                border: '1px solid #e5e7eb',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                overflow: 'hidden',
            }}>
                {section.jobs.map((job, jobIdx) => (
                    <JobRow
                        key={job.scheduledEventId ?? 'nursery-' + crewIdx}
                        job={job}
                        jobIdx={jobIdx}
                        crewIdx={crewIdx}
                        isLast={jobIdx === section.jobs.length - 1}
                        onUpdateRow={onUpdateRow}
                        onAddRow={onAddRow}
                        onRemoveRow={onRemoveRow}
                        nurseryTasks={nurseryTasks}
                    />
                ))}
            </div>
        </div>
    );
}

// -- JobRow
function JobRow({ job, jobIdx, crewIdx, isLast, onUpdateRow, onAddRow, onRemoveRow, nurseryTasks }) {
    const isNursery = job.scheduledEventId === null;

    return (
        <div style={{
            padding: '0.75rem 1rem',
            borderBottom: isLast ? 'none' : '1px solid #f3f4f6',
            backgroundColor: isNursery ? '#f9fafb' : '#ffffff',
        }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827', marginBottom: '0.5rem' }}>
                {job.label}
            </div>

            {job.rows.map((row, rowIdx) => {
                const locked = !!row.approved;

                return (
                    <div
                        key={row.id ?? `new-${rowIdx}`}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: '0.4rem',
                            flexWrap: 'wrap',
                            opacity: locked ? 0.7 : 1,
                        }}
                    >
                        <input
                            type="time"
                            value={row.timeIn}
                            disabled={locked}
                            onChange={(e) => onUpdateRow(crewIdx, jobIdx, rowIdx, 'timeIn', e.target.value)}
                            style={timeInputStyle}
                        />
                        <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>–</span>
                        <input
                            type="time"
                            value={row.timeOut}
                            disabled={locked}
                            onChange={(e) => onUpdateRow(crewIdx, jobIdx, rowIdx, 'timeOut', e.target.value)}
                            style={timeInputStyle}
                        />
                        {/* Task selector, only on Nursery rows */}
                        {isNursery && (
                            <select
                                value={row.nurseryTaskId ?? ''}
                                disabled={locked}
                                onChange={(e) =>
                                    onUpdateRow(crewIdx, jobIdx, rowIdx, 'nurseryTaskId', e.target.value ? Number(e.target.value) : null)
                                }
                                style={taskSelectStyle}
                            >
                                <option value="">Select task…</option>
                                {nurseryTasks.map((t) => (
                                    <option key={t.id} value={t.id}>{t.task}</option>
                                ))}
                            </select>
                        )}

                        {locked ? (
                            <span style={{ fontSize: '0.72rem', color: '#9ca3af', marginLeft: '0.25rem' }}>
                                Approved
                            </span>
                        ) : (
                            <Button color="link" size="md"
                                onClick={() => {
                                    if (job.rows.length === 1) {
                                        onUpdateRow(crewIdx, jobIdx, rowIdx, 'timeIn', '');
                                        onUpdateRow(crewIdx, jobIdx, rowIdx, 'timeOut', '');
                                        onUpdateRow(crewIdx, jobIdx, rowIdx, 'nurseryTaskId', null);
                                    } else {
                                        onRemoveRow(crewIdx, jobIdx, rowIdx);
                                    }
                                }}
                                style={{ color: '#ef4444', padding: '0.25rem 0.4rem', lineHeight: 1 }}>
                                <BsTrash size={20} />
                            </Button>
                        )}
                    </div>
                );
            })}

            {/* Add is a job-level action - always available, even when every
                existing row is approved. New rows insert as unapproved (id: null). */}
            <Button color="primary" size="sm" onClick={() => onAddRow(crewIdx, jobIdx)}
                style={{ padding: '0.25rem 0.6rem', lineHeight: 1, marginTop: '0.25rem' }}>
                <BsPlus size={20} />
            </Button>
        </div>
    );
}

const taskSelectStyle = {
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    padding: '0.3rem 0.5rem',
    fontSize: '0.85rem',
    color: '#374151',
    backgroundColor: '#ffffff',
    minWidth: '180px',
};

const timeInputStyle = {
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    padding: '0.3rem 0.5rem',
    fontSize: '0.85rem',
    color: '#374151',
    backgroundColor: '#ffffff',
};

export default TimeEntry;